<template>
    <div class="table-wrapper" ref="table-wrapper">
        <loading-spinner class="loading-spinner" v-if="loading" />
        <transition name="fade">
            <b-table
                v-if="report !== null"
                striped
                :tbody-tr-class="rowClass"
                hover
                responsive
                outlined
                :bordered="fullMode"
                :fields="fields"
                :items="items"
                :busy="loading"
                :class="`report-table-client mode-${mode}`"
                :sticky-header="stickyHeader"
            >
                <template v-slot:cell(baseProduct)="data">
                    {{ data.item.baseProduct }}
                    <div>
                        <span class="text-info">{{ $t("collection") }}:</span>
                        {{ data.item.collection }}
                    </div>
                </template>

                <template v-slot:cell(po_number)="data">
                    <div>
                        <span class="text-info">{{ data.item.po_number }}</span>
                        {{ $t("due_on_date", { date: data.item.due_date }) }}
                    </div>
                    <div>
                        <span class="text-info">{{ $t("order_date") }}:</span>
                        {{ data.item.order_date }}
                    </div>
                </template>

                <template v-slot:cell(problemCategory)="data">
                    <template v-if="data.item.problem">
                        {{ data.item.problemCategory }}
                    </template>
                </template>

                <template v-slot:cell(problem)="data">
                    <template v-if="data.item.problem">
                        {{ data.item.problem }}
                    </template>
                </template>

                <template v-slot:cell(comment)="data">
                    <p class="col-comment">
                        {{ data.item.comment }}
                    </p>
                </template>

                <template v-slot:cell(photos)="data">
                    <viewer
                        v-if="data.item.photos.length"
                        :options="viewerOptions"
                    >
                        <b-img
                            thumbnail
                            v-for="{ id, url, thumb, file_name } in data.item
                                .photos"
                            :src="thumb"
                            :data-source="url"
                            :key="id"
                            :alt="file_name"
                        />
                    </viewer>
                </template>

                <template v-slot:cell(productHistory)="data">
                    <span v-html="data.item.productHistory"></span>
                </template>
            </b-table>
        </transition>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import "viewerjs/dist/viewer.css";
import Viewer from "v-viewer/src/component.vue";
import LoadingSpinner from "../Common/LoadingSpinner";

export default {
  components: {
    Viewer,
    LoadingSpinner
  },
  props: {
    sortable: Boolean,
    reportId: {
      type: String,
      default: null
    },
    mode: {
      type: String,
      default: "full"
    }
  },
  data() {
    return {
      loading: true,
      report: null,
      productAttributes: [],
      isShowSerialNumber: false,
      items: [],
      stickyHeader: true,
      modes: {
        executive: [
          "productReference",
          "serial_number",
          "baseProduct",
          "productQuantity",
          "po_number",
          "due_date"
        ]
      },
      viewerOptions: {
        toolbar: {
          zoomIn: 4,
          zoomOut: 4,
          oneToOne: 4,
          reset: 4,
          prev: 4,
          play: {
            show: 4,
            size: "large"
          },
          next: 4,
          rotateLeft: false,
          rotateRight: false,
          flipHorizontal: false,
          flipVertical: false
        },
        url: "data-source"
      }
    };
  },
  computed: {
    fullMode() {
      return this.mode === "full";
    },
    fields() {
      let fields = [
        {
          key: "productReference",
          label: this.$t("reference"),
          class: "col-sticky-1 reference",
          sortable: true,
          stickyColumn: true
        }
      ];

      if (this.isShowSerialNumber) {
        fields.push({
          key: "serial_number",
          label: this.$t("serial_number"),
          class: "col-sticky-2 serial",
          stickyColumn: true,
          sortable: true
        });
      }

      fields.push(
        {
          key: "due_date",
          label: this.$t("due_date"),
          sortable: true
        },
        {
          key: "baseProduct",
          label: this.$t("product"),
          class: "baseProduct",
          sortable: true
        },
        {
          key: "po_number",
          label: this.$t("purchase_order"),
          class: "purchaseOrder",
          sortable: true
        }
      );

      this.productAttributes.forEach(a => {
        fields.push({
          key: "productAttribute" + a.id,
          label: a.name,
          sortable: true,
          class: "product-attribute " + a.type
        });
      });

      fields.push(
        {
          key: "productQuantity",
          label: this.$t("Product Quantity"),
          sortable: true
        },
        {
          key: "validatedCount",
          label: this.$t("validated"),
          sortable: true
        },
        {
          key: "passCount",
          label: this.$t("pass"),
          sortable: true,
          class: "numeric pass"
        },
        {
          key: "failCount",
          label: this.$t("fail"),
          sortable: true,
          class: "numeric fail"
        },
        {
          key: "repairCount",
          label: this.$t("repaired"),
          sortable: true,
          class: "numeric repaired"
        },
        {
          key: "problemCategory",
          label: this.$t("defect_category"),
          sortable: true
        },
        {
          key: "problem",
          label: this.$t("defect"),
          sortable: true
        }
      );

      this.report.template.columns.forEach(c => {
        fields.push({
          key: "customColumn" + c.id,
          label: c.name,
          sortable: true,
          class: "text-capitalize custom " + c.type,
          formatter: value => {
            if (c.type == "float" && value) {
              return value.toFixed(2);
            } else if (c.type == "bool") {
              return this.formatBool(value);
            } else {
              return value;
            }
          }
        });
      });

      fields.push(
        {
          key: "comment",
          label: this.$t("comment"),
          class: "comment"
        },
        {
          key: "photos",
          label: this.$t("photos")
        },
        {
          key: "productHistory",
          label: this.$t("Product History"),
          class: "productHistory"
        }
      );

      return fields.filter(
        f => this.mode === "full" || this.modes[this.mode].includes(f.key)
      );
    }
  },
  mounted() {
    this.getReport().finally(() => {
      this.loading = false;
      // Après que 'report' et 'items' soient chargés,
      // on attend le rendu final du tableau
      this.$nextTick(() => {
        this.adjustStickyColumns();
      });
    });
    this.getTableHeight();
  },
  created() {
    window.addEventListener("resize", _.debounce(this.getTableHeight, 100));
  },
  destroyed() {
    window.removeEventListener("resize", _.debounce(this.getTableHeight, 100));
  },
  methods: {
    rowClass(item, type) {
      if (!item || type !== "row") return;
      if (item.productHistory !== "") return "table-warning";
    },
    getTableHeight() {
      let viewportHeight = window.innerHeight;
      let tableWrapper = this.$refs["table-wrapper"].getBoundingClientRect();

      // Different height based on active mode
      if (this.fullMode) {
        this.stickyHeader = viewportHeight - (tableWrapper.top + 24) + "px";
      } else {
        this.stickyHeader = viewportHeight * 0.75 + "px";
      }
    },
    adjustStickyColumns() {
      // Sélectionne TOUTES les occurrences du first-col (header + body)
      const firstColAll = this.$el.querySelectorAll(
        ".col-sticky-1.b-table-sticky-column"
      );
      // Sélectionne TOUTES les occurrences du second-col (header + body)
      const secondColAll = this.$el.querySelectorAll(
        ".col-sticky-2.b-table-sticky-column"
      );
      if (!firstColAll.length || !secondColAll.length) return;

      // On prend la largeur la plus grande du 1er col (header ou body)
      let maxWidth = 0;
      firstColAll.forEach(col => {
        const w = col.getBoundingClientRect().width;
        if (w > maxWidth) maxWidth = w;
      });

      // On applique left: [maxWidth] px sur toutes les 2e colonnes
      secondColAll.forEach(col => {
        col.style.left = maxWidth + "px";
      });
    },
    formatBool(v) {
      if (v === null) {
        return null;
      }

      return v === true ? "Yes" : "No";
    },
    getReport() {
      return new Promise(resolve => {
        axios
          .get("/api/reports/" + this.reportId + "/data", {
            params: {
              report: this.reportId,
              mode: this.mode
            }
          })
          .then(response => {
            this.report = response.data.report;
            this.productAttributes = response.data.product_attributes;
            this.items = response.data.rows;
            this.items.forEach(
              function(item) {
                if (item.serial_number !== null) {
                  this.isShowSerialNumber = true;
                  return;
                }
              }.bind(this)
            );
          })
          .finally(resolve);
      });
    }
  }
};
</script>

<style>
.report-table-client {
  th.b-table-sticky-column {
    position: sticky !important;
    left: 0;
  }

  .b-table-sticky-column:nth-of-type(1) {
    font-weight: bold;
  }

  .b-table-sticky-column:nth-of-type(2) {
  }
}

.productHistory {
  white-space: pre !important;
}
</style>
